var _SovosLinearProgress;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { forwardRef, useMemo } from 'react';
import { array, arrayOf, bool, exact, elementType, func, node, number, object, oneOfType, string, shape } from 'prop-types';
import clsx from 'clsx';
import { Search } from 'mosaic-react-icons';
import { DataGridPro as DataGrid, gridClasses } from '@mui/x-data-grid-pro';
import { styled, useTheme } from '@mui/material/styles';
import { spacingToNumber } from '../internals/utils';
import SovosCheckbox, { sovosCheckboxClasses } from '../sovos-checkbox';
import SovosLinearProgress from '../sovos-linear-progress/SovosLinearProgress';
import NoResults from '../internals/components/table-group/NoResults';
import { bulkActionsPropTypes } from '../internals/components/table-group/Toolbar';
import Toolbar from './components/Toolbar';
import SovosDataGridPagination from './SovosDataGridPagination';
const Grid = Component => styled(Component)(_ref => {
  let {
    disableColumnResize,
    checkboxSelection,
    theme: {
      palette,
      spacing,
      typography
    }
  } = _ref;
  return {
    backgroundColor: palette.background.default,
    borderRadius: 0,
    border: 'none',
    ["& .".concat(gridClasses.columnHeaders)]: {
      borderRadius: 'unset',
      ["& .".concat(gridClasses.columnHeaderTitle)]: {
        ...typography.body2
      },
      ["& .".concat(gridClasses['columnHeader--filledGroup'], ", & .").concat(gridClasses['columnHeader--emptyGroup'])]: {
        backgroundColor: palette.grey[200],
        color: palette.text.primary,
        '&:last-of-type': {
          minWidth: '100% !important'
        }
      },
      ["& .".concat(gridClasses['columnHeader--emptyGroup'])]: {
        '&:first-of-type': {
          width: checkboxSelection ? "".concat(spacing(4), " !important") : null,
          minWidth: checkboxSelection ? "".concat(spacing(4), " !important") : null
        }
      },
      ["& .".concat(gridClasses['columnHeader--filledGroup'])]: {
        padding: checkboxSelection ? "0 ".concat(spacing(2), " 0 ").concat(spacing(0.5)) : null
      },
      ["& .".concat(gridClasses['columnHeader--filledGroup'], " ~ .").concat(gridClasses['columnHeader--filledGroup'])]: {
        padding: checkboxSelection ? '0 10px' : null
      },
      ["& .".concat(gridClasses.columnHeadersInner, "  [role=\"row\"] .").concat(gridClasses.columnHeader)]: {
        '&:first-of-type': {
          paddingLeft: spacing(0.5)
        }
      }
    },
    ["& .".concat(gridClasses.row, " .").concat(gridClasses.cell)]: {
      '&:first-of-type': {
        paddingLeft: spacing(0.5)
      }
    },
    ["& .".concat(gridClasses.columnHeaderTitleContainer)]: {
      border: 'none'
    },
    ["& .".concat(gridClasses.columnHeader)]: {
      border: 'none',
      color: palette.text.secondary,
      ["& .".concat(gridClasses.columnHeaderTitleContainer)]: {
        padding: 0
      }
    },
    ["& .".concat(gridClasses.iconSeparator)]: {
      display: disableColumnResize ? 'none' : null,
      height: spacing(2)
    },
    ["& .".concat(gridClasses['columnSeparator--resizable'])]: {
      color: palette.divider,
      '&:hover': {
        color: palette.primary.main
      }
    },
    ["& .".concat(gridClasses.cellCheckbox)]: {
      // MUI doesn't allow to change the following width properties, once it's being set on the element.style
      minWidth: "".concat(spacing(4), " !important"),
      maxWidth: "".concat(spacing(4), " !important"),
      justifyContent: 'right',
      '& + div': {
        padding: "0 ".concat(spacing(2), " 0 ").concat(spacing(0.5))
      }
    },
    ["& .".concat(gridClasses.columnHeaderCheckbox)]: {
      // MUI doesn't allow to change the following width properties, once it's being set on the element.style
      minWidth: "".concat(spacing(4), " !important"),
      maxWidth: "".concat(spacing(4), " !important"),
      width: "".concat(spacing(4), " !important"),
      ["& .".concat(gridClasses.columnHeaderTitleContainer)]: {
        justifyContent: 'right'
      },
      ["& .".concat(gridClasses.iconSeparator)]: {
        display: 'none'
      },
      '& + div': {
        padding: "0 ".concat(spacing(2), " 0 ").concat(spacing(0.5))
      }
    },
    '& [aria-sort="ascending"], & [aria-sort="descending"]': {
      color: palette.primary.main
    },
    ["& .".concat(gridClasses.iconButtonContainer)]: {
      '& button': {
        display: 'none'
      }
    },
    ["& .".concat(gridClasses.withBorderColor)]: {
      borderColor: palette.divider
    }
  };
});
const Root = styled('div')(() => ({
  '&.sovosDataGrid__autoHeight': {
    height: '100% !important'
  },
  '&.sovosDataGrid': {
    display: 'flex',
    flexDirection: 'column',
    height: '65vh'
  },
  ["& .".concat(gridClasses.virtualScroller)]: {
    overflowY: 'unset !important'
  },
  '& .sovosCheckbox ': {
    ["&.".concat(sovosCheckboxClasses.focusVisible)]: {
      '&::after': {
        outlineOffset: -3
      }
    }
  }
}));
const GridCellCheckboxRenderer = /*#__PURE__*/forwardRef((props, ref) => /*#__PURE__*/React.createElement(SovosCheckbox, _extends({
  size: "tiny",
  ref: ref
}, props)));
const GridLoadingOverlay = () => _SovosLinearProgress || (_SovosLinearProgress = /*#__PURE__*/React.createElement(SovosLinearProgress, {
  "aria-label": "Loading Data",
  className: "sovosDataGrid__loading"
}));
const SovosDataGridNext = _ref2 => {
  let {
    autoHeight = false,
    className,
    checkboxSelection,
    columns,
    Component = DataGrid,
    'data-testid': dataTestId,
    disableColumnMenu = true,
    disableColumnResize = true,
    disableRowSelectionOnClick = true,
    isLoading = false,
    localeText = {
      toolbarColumns: '',
      toolbarFilters: '',
      toolbarDensity: '',
      toolbarExport: ''
    },
    pagination = true,
    pageSizeOptions = [20, 50, 100],
    rows,
    rowSelectionModel,
    slots,
    slotProps,
    sx,
    ToolbarProps,
    ...rest
  } = _ref2;
  const {
    spacing
  } = useTheme();
  const StyledGrid = useMemo(() => Grid(Component), [Component]);
  return /*#__PURE__*/React.createElement(Root, {
    className: clsx('sovosDataGrid', className, {
      sovosDataGrid__autoHeight: autoHeight
    }),
    "data-testid": dataTestId,
    sx: sx
  }, /*#__PURE__*/React.createElement(StyledGrid, _extends({
    autoHeight: autoHeight,
    checkboxSelection: checkboxSelection,
    columns: columns,
    columnHeaderHeight: spacingToNumber(spacing(4)),
    "data-testid": dataTestId,
    disableColumnMenu: disableColumnMenu,
    disableColumnResize: disableColumnResize,
    disableRowSelectionOnClick: disableRowSelectionOnClick,
    experimentalFeatures: {
      columnGrouping: true
    },
    loading: isLoading,
    localeText: localeText,
    pagination: pagination,
    pageSizeOptions: pageSizeOptions,
    rows: rows,
    rowHeight: spacingToNumber(spacing(5)),
    rowSelectionModel: rowSelectionModel,
    slots: {
      ...(slots || {}),
      baseCheckbox: (slots === null || slots === void 0 ? void 0 : slots.baseCheckbox) || GridCellCheckboxRenderer,
      quickFilterIcon: (slots === null || slots === void 0 ? void 0 : slots.quickFilterIcon) || Search,
      toolbar: (slots === null || slots === void 0 ? void 0 : slots.toolbar) || Toolbar,
      loadingOverlay: (slots === null || slots === void 0 ? void 0 : slots.loadingOverlay) || GridLoadingOverlay,
      noResultsOverlay: (slots === null || slots === void 0 ? void 0 : slots.noResultsOverlay) || NoResults,
      noRowsOverlay: (slots === null || slots === void 0 ? void 0 : slots.noRowsOverlay) || NoResults,
      footer: (slots === null || slots === void 0 ? void 0 : slots.footer) || SovosDataGridPagination
    },
    slotProps: {
      ...(slotProps || {}),
      toolbar: (slotProps === null || slotProps === void 0 ? void 0 : slotProps.toolbar) || {
        isLoading,
        rowSelectionModel,
        ToolbarProps
      }
    }
  }, rest)));
};
SovosDataGridNext.propTypes = {
  /**
   * Extend the class name applied to the root element
   */
  autoHeight: bool,
  /**
   * If true, add a checkbox that allows users to select rows
   */
  checkboxSelection: bool,
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * Data grid column definitions
   */
  columns: arrayOf(object).isRequired,
  /**
   * Material UI data grid component. Can be overriden to use DataGrid
   * Premium, defaults to DataGrid Pro
   */
  Component: oneOfType([elementType, func]),
  /**
   * @ignore
   */
  'data-testid': string,
  /**
   * Disale column menu
   */
  disableColumnMenu: bool,
  /**
   * Disable column resize
   */
  disableColumnResize: bool,
  /**
   * Disable selecting rows on click
   */
  disableRowSelectionOnClick: bool,
  /**
   * When true an indeterminate progress bar is displayed.
   */
  isLoading: bool,
  /**
   * @ignore
   */
  localeText: object,
  /**
   * Callback fired when rows are selected in the data grid, controls
   * hiding and showing of selection checkboxes. func(params:
   * GridSelectionsModel, details: GridCallbackDetails)
   */
  onSelectionModelChange: func,
  /**
   * Toggle pagination functionality
   */
  pagination: bool,
  /**
   * Options for page sizes
   */
  pageSizeOptions: array,
  /**
   * Data grid rows
   */
  rows: arrayOf(object).isRequired,
  /**
   * Set the selection model of the grid.
   */
  rowSelectionModel: oneOfType([arrayOf(oneOfType([string, number])), string, number]),
  /**
   * @ignore
   */
  slots: object,
  /**
   * @ignore
   */
  slotProps: object,
  /**
   * The system prop that allows defining system overrides as well as
   * additional CSS styles.
   */
  sx: oneOfType([arrayOf(oneOfType([func, object, bool])), func, object]),
  /**
   * An object of props for the Toolbar:
   *
   * - `actionIconButtons`: arrayOf(node)
   *
   *   - Specifies additional icon buttons to be rendered in the toolbar. Array
   *       of SovosIconButton
   * - `bulkActions`: bulkActionsPropTypes
   *
   *   - When present, selecting any rows will open the bulk actions bar. Array
   *       of objects with an `icon`, `tooltipText`, and `onClick`
   * - Labels: object
   *
   *   - Button tooltips and text for the bulk actions
   * - `quickFilters`: arrayOf(node)
   *
   *   - Quick filter elements to be rendered int the toolbar. Array of
   *       `SovosToolbarToggles`, `SovosToolabarDateSelector`,
   *       `SovosToolbarDropdown`, or other elements wrapped in a
   *       `SovosToolbarGroup`
   * - `showColumnVisibility`: bool
   *
   *   - Show a button to open the column visibility panel
   * - `showDensitySelector`: bool
   *
   *   - Show a button to open the row density selector
   * - `showFilters`: bool
   *
   *   - Show a button to opne the filter panel
   * - `showSearch`
   *
   *   - Show the Material UI DataGrid quick filter (search) box
   * - `toolbarTitle`: node
   *
   *   - When present, the toolbar will be rendered with a title
   */
  ToolbarProps: exact({
    actionIconButtons: arrayOf(node),
    bulkActions: bulkActionsPropTypes,
    labels: shape({
      bulkActions: string,
      columnButtonTooltip: string,
      filterButtonTooltip: string,
      densityTooltip: string
    }),
    quickFilters: arrayOf(node),
    showColumnSelector: bool,
    showDensityToggle: bool,
    showFilters: bool,
    showSearch: bool,
    toolbarTitle: node
  })
};
SovosDataGridNext.baseComponent = {
  name: 'Data Grid',
  link: 'https://mui.com/x/react-data-grid/'
};
export default SovosDataGridNext;